import { useContext, useState } from "react";
import * as xls from "xlsx";
import Dropzone from "react-dropzone";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { closeSnackbar, enqueueSnackbar } from "notistack";
import { context } from "utils/Provider";
import { useTableInitializer } from "../../matcher/services/useTableInitializer";
import { styled } from "@mui/system";
import colors from "assets/theme-dark/base/colors";
import { FileCard } from "./FileCard";
import { SheetSelectorDialog } from "./SheetSelectorDialog";
import { useFileProcessor } from "../hooks/useFileProcessor";

interface DropzoneProps {
  buttonDisabled: boolean;
  isMutating: boolean;
  setIsMutating: React.Dispatch<React.SetStateAction<boolean>>;
  fileToUpload: File | null;
  setFileToUpload: React.Dispatch<React.SetStateAction<File | null>>;
  setSelectedSheetName: React.Dispatch<React.SetStateAction<string | null>>;
  onFileUpload?: (file: File, headers: string[]) => Promise<void>;
  mutationParam: "azure" | "matcher";
  dropzoneWidth?: string;
}

const StyledCard = styled(MDBox)<{ dropzoneWidth?: string }>`
  width: ${({ dropzoneWidth }) => dropzoneWidth || "100%"};
  background: ${colors.background.default};
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  padding: 1rem;
  border: 1px solid ${colors.grey[300]};
`;

export function DropzoneComponent({
  buttonDisabled,
  isMutating,
  setIsMutating,
  fileToUpload,
  setFileToUpload,
  setSelectedSheetName,
  onFileUpload,
  mutationParam,
  dropzoneWidth,
}: DropzoneProps) {
  const { setIsLoading } = useContext(context);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  const [showSheetDialog, setShowSheetDialog] = useState(false);
  const [currentFile, setCurrentFile] = useState<File | null>(null);
  const { initializeTable } = useTableInitializer({ onFileUpload });
  const { processCSV, processExcel } = useFileProcessor({
    setFileToUpload,
    setSelectedSheetName,
    setIsLoading,
    initializeTable,
    mutationParam,
  });

  const handleOnDropAccepted = async (file: File) => {
    setIsMutating(true);
    setIsLoading(true);
    const preparingKey = enqueueSnackbar("Preparing file for upload...", { variant: "info", persist: true });

    try {
      if (file.type === "text/csv") {
        await processCSV(file);
      } else {
        const arrayBuffer = await file.arrayBuffer();
        const workbook = xls.read(arrayBuffer, { type: "array", bookProps: true, bookSheets: true });

        if (workbook.SheetNames.length === 1) {
          await processExcel(file, workbook.SheetNames[0]);
        } else {
          setSheetNames(workbook.SheetNames);
          setCurrentFile(file);
          setShowSheetDialog(true);
        }
      }
    } catch (error) {
      setErrorMessage("Failed to process file");
    } finally {
      closeSnackbar(preparingKey);
      setIsLoading(false);
      setIsMutating(false);
    }
  };

  const handleSheetSelect = async (sheetName: string | null) => {
    if (sheetName && currentFile) {
      setSelectedSheetName(sheetName);
      await processExcel(currentFile, sheetName);
    }
    setSheetNames([]);
    setCurrentFile(null);
    setShowSheetDialog(false);
  };

  return (
    <>
      <Dropzone
        multiple={false}
        accept={{
          "text/plain": [".txt"],
          "text/csv": [".csv"],
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
          "application/vnd.ms-excel": [".xls"],
        }}
        onDropRejected={() =>
          setErrorMessage("Invalid file type. Please upload a .csv or .xlsx or properly delimited .txt file.")
        }
        onDropAccepted={async ([file]) => handleOnDropAccepted(file)}
      >
        {({ getRootProps, getInputProps }) => (
          <section>
            {fileToUpload ? (
              <FileCard
                file={fileToUpload}
                onRemove={() => {
                  setFileToUpload(null);
                }}
                disabled={buttonDisabled || isMutating}
                dropzoneWidth={dropzoneWidth}
              />
            ) : (
              <StyledCard dropzoneWidth={dropzoneWidth}>
                <MDBox
                  {...getRootProps()}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "15rem",
                    alignItems: "center",
                    justifyContent: "center",
                    userSelect: "none",
                    cursor: "pointer",
                    border: "2px dashed",
                    borderRadius: "8px",
                    borderColor: colors.grey[500],
                  }}
                >
                  <input {...getInputProps()} />
                  <MDBox
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: 2,
                      userSelect: "none",
                    }}
                  >
                    <MDBox
                      sx={{
                        border: "1px dashed",
                        borderRadius: "50%",
                        borderColor: colors.grey[500],
                        padding: 3,
                      }}
                    >
                      <FileUploadIcon
                        sx={{
                          fontSize: 32,
                          color: colors.grey[500],
                        }}
                        aria-hidden="true"
                      />
                    </MDBox>
                    <section
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <MDTypography variant="body1" sx={{ color: colors.text.main, textAlign: "center", px: "0.5rem" }}>
                        Drag &apos;n&apos; drop a file here, or click to select one
                      </MDTypography>
                      <MDTypography variant="body2" sx={{ color: colors.grey[500], textAlign: "center", px: "0.5rem" }}>
                        Only .csv, .xlsx and .txt files are supported
                      </MDTypography>
                    </section>
                  </MDBox>
                </MDBox>
              </StyledCard>
            )}
            {errorMessage && (
              <MDTypography variant="body2" sx={{ color: "error.main", textAlign: "center", mt: 1 }}>
                {errorMessage}
              </MDTypography>
            )}
          </section>
        )}
      </Dropzone>
      <SheetSelectorDialog
        open={showSheetDialog}
        sheetNames={sheetNames}
        onClose={() => setShowSheetDialog(false)}
        onSelect={handleSheetSelect}
      />
    </>
  );
}
